/**
 * QA Grammar Panel
 * 
 * Displays grammar/spelling errors with highlighting, tone suggestions,
 * style recommendations, and readability metrics.
 */

import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { CheckCircle, XCircle, AlertTriangle, RefreshCw, BookOpen } from 'lucide-react';
import { checkGrammar, type GrammarCheckResult } from '../../api/qaApi';
import { useToastStore } from '../../stores/toast';

export interface QAGrammarPanelProps {
  reportId: string;
}

export function QAGrammarPanel({ reportId }: QAGrammarPanelProps) {
  const [isChecking, setIsChecking] = useState(false);
  const { addToast } = useToastStore();
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['qa-grammar', reportId],
    queryFn: () => checkGrammar(reportId),
    enabled: false, // Don't auto-fetch, require manual trigger
    retry: false
  });
  
  const checkMutation = useMutation({
    mutationFn: () => checkGrammar(reportId),
    onSuccess: () => {
      refetch();
    },
    onError: (error: any) => {
      console.error('[QA Grammar] Check failed:', error);
      const isTimeout = error?.code === 'ECONNABORTED' || error?.message?.includes('timeout');
      addToast({
        type: 'error',
        message: isTimeout
          ? 'Grammar check timed out — the AI service is busy. Please try again in a moment.'
          : `Grammar check failed: ${error?.response?.data?.detail || error?.message || 'Service temporarily unavailable'}`,
        duration: 6000,
      });
    }
  });
  
  const handleCheck = async () => {
    setIsChecking(true);
    try {
      await checkMutation.mutateAsync();
    } finally {
      setIsChecking(false);
    }
  };
  
  if (!data && !isLoading && !error) {
    return (
      <Card>
        <div className="text-center py-8">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Click "Check Grammar" to analyze grammar, spelling, tone, and style
          </p>
          <Button
            onClick={handleCheck}
            disabled={isChecking}
            leftIcon={isChecking ? <LoadingSpinner /> : <RefreshCw className="w-4 h-4" />}
          >
            {isChecking ? 'Checking...' : 'Check Grammar'}
          </Button>
        </div>
      </Card>
    );
  }
  
  if (isLoading || isChecking) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Checking grammar...</span>
        </div>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card>
        <div className="text-center py-8">
          <AlertTriangle className="h-12 w-12 mx-auto text-red-400 mb-4" />
          <p className="text-red-600 dark:text-red-400 mb-4">
            Failed to check grammar: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <Button onClick={handleCheck} variant="outline">
            Retry
          </Button>
        </div>
      </Card>
    );
  }
  
  if (!data || data.error) {
    return (
      <Card>
        <div className="text-center py-8">
          <AlertTriangle className="h-12 w-12 mx-auto text-yellow-400 mb-4" />
          <p className="text-yellow-600 dark:text-yellow-400 mb-4">
            {data?.error || 'Grammar checking not available'}
          </p>
          <Button onClick={handleCheck} variant="outline">
            Try Again
          </Button>
        </div>
      </Card>
    );
  }
  
  const results: GrammarCheckResult = data;
  const errorCount = results.errors.filter(e => e.severity === 'error').length;
  const warningCount = results.errors.filter(e => e.severity === 'warning').length;
  const suggestionCount = results.errors.filter(e => e.severity === 'suggestion').length;
  
  return (
    <Card>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold mb-2">Grammar & Style Check</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={results.overall_passed ? 'success' : 'error'}>
              {results.overall_passed ? 'Passed' : 'Failed'}
            </Badge>
            {results.readability_score !== undefined && (
              <Badge variant="info" className="flex items-center gap-1">
                <BookOpen className="w-3 h-3" />
                Readability: {results.readability_score}/100
              </Badge>
            )}
            {errorCount > 0 && (
              <Badge variant="error">{errorCount} error{errorCount !== 1 ? 's' : ''}</Badge>
            )}
            {warningCount > 0 && (
              <Badge variant="warning">{warningCount} warning{warningCount !== 1 ? 's' : ''}</Badge>
            )}
            {suggestionCount > 0 && (
              <Badge variant="info">{suggestionCount} suggestion{suggestionCount !== 1 ? 's' : ''}</Badge>
            )}
            {results.checked_at && (
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Checked: {new Date(results.checked_at).toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <Button
          onClick={handleCheck}
          disabled={isChecking}
          variant="outline"
          size="sm"
          leftIcon={isChecking ? <LoadingSpinner /> : <RefreshCw className="w-4 h-4" />}
        >
          {isChecking ? 'Checking...' : 'Re-check'}
        </Button>
      </div>
      
      {results.errors.length > 0 && (
        <div className="space-y-4 mb-6">
          <h4 className="font-medium text-theme-text">Errors & Issues</h4>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {results.errors.map((error, idx) => {
              const severityColors = {
                error: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200',
                warning: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200',
                suggestion: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200'
              };
              
              return (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border ${severityColors[error.severity]}`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={error.severity === 'error' ? 'error' : error.severity === 'warning' ? 'warning' : 'info'} size="sm">
                        {error.type}
                      </Badge>
                      <Badge variant="default" size="sm">
                        {error.severity}
                      </Badge>
                      {error.line_number && (
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          Line {error.line_number}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="text-sm font-medium mb-1">Original:</div>
                    <div className="text-sm bg-white dark:bg-gray-800 p-2 rounded border font-mono">
                      {error.original_text}
                    </div>
                    {error.suggested_text && (
                      <>
                        <div className="text-sm font-medium mt-2 mb-1">Suggested:</div>
                        <div className="text-sm bg-white dark:bg-gray-800 p-2 rounded border font-mono text-green-700 dark:text-green-400">
                          {error.suggested_text}
                        </div>
                      </>
                    )}
                    {error.explanation && (
                      <div className="text-xs mt-1 italic text-gray-600 dark:text-gray-400">
                        {error.explanation}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {results.tone_suggestions.length > 0 && (
        <div className="mb-6">
          <h4 className="font-medium text-theme-text mb-3">Tone Suggestions</h4>
          <div className="space-y-2">
            {results.tone_suggestions.map((suggestion, idx) => (
              <div
                key={idx}
                className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800"
              >
                <div className="text-sm font-medium mb-1">{suggestion.section}</div>
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Current: <span className="italic">{suggestion.current_tone}</span> → 
                  Suggested: <span className="font-medium">{suggestion.suggested_tone}</span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {suggestion.reason}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {results.style_inconsistencies.length > 0 && (
        <div>
          <h4 className="font-medium text-theme-text mb-3">Style Inconsistencies</h4>
          <div className="space-y-2">
            {results.style_inconsistencies.map((inconsistency, idx) => (
              <div
                key={idx}
                className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800"
              >
                <div className="text-sm font-medium mb-1">{inconsistency.issue}</div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Found {inconsistency.occurrences} occurrence{inconsistency.occurrences !== 1 ? 's' : ''}
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  {inconsistency.recommendation}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {results.errors.length === 0 && results.tone_suggestions.length === 0 && results.style_inconsistencies.length === 0 && (
        <div className="text-center py-8">
          <CheckCircle className="h-12 w-12 mx-auto text-green-600 dark:text-green-400 mb-4" />
          <p className="text-green-600 dark:text-green-400 font-medium">
            No grammar, spelling, tone, or style issues found!
          </p>
        </div>
      )}
    </Card>
  );
}
